import React, { useMemo, useCallback } from 'react'
import { Button } from '../ui/button'
import { StatusBadge } from '../ui/badge'
import { CardType, GameRoomStatus, Status } from '@/redux/types/room'
import OGECardDropdown from '../OGE/OGECardDropdown'
import { Users, Bot, Clock, User, Lock, LockOpen } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import {
  NegotiationMember,
  NegotiationType,
} from '@/redux/types/negotiationWebsocket'

import { useGameControls } from '@/hooks/useGameControls'
import DashboardControls from './DashboardControls'

const bgColors = [
  'bg-gradient-to-br from-[#023572] to-[#012550]',
  'bg-gradient-to-br from-[#008BC0] to-[#006690]',
  'bg-gradient-to-br from-[#008F91] to-[#006668]',
  'bg-gradient-to-br from-[#4A66AC] to-[#334878]',
]
interface RoomCardProps {
  cardType: CardType
  roomId: string
  memberEmail?: string
  inviteCode?: string
  roomName?: string
  playingAs?: string
  status: GameRoomStatus | Status
  showBadge?: boolean
  onDeleteClick?: () => void
  onLockedClick?: (roomId: string, is_locked: boolean) => void
  onEditClick?: () => void
  onDuplicateClick?: () => void
  onExportClick?: () => void
  onLeaveClick?: () => void
  buttonText: string
  onButtonClick?: () => void
  customBgColor?: string
  roomIdPrefix?: string
  inviter?: string
  members?: NegotiationMember[]
  isDisabled?: boolean
  showDropdown?: boolean
  isProfessor?: boolean
  pendingAdjudicationsCount?: number
  advisorsCount?: number
  playersCount?: number
  negotiationType?: string
  isGameReadyToStart?: boolean
  scenarioTitle?: string
  isLocked?: boolean
  isOwner?: boolean
  isSimulating?: boolean
}

const RoomCard: React.FC<RoomCardProps> = ({
  cardType,
  roomId,
  memberEmail,
  inviteCode,
  roomName,
  playingAs,
  status,
  showBadge = true,
  onDeleteClick,
  onEditClick,
  onLockedClick,
  onDuplicateClick,
  onExportClick,
  onLeaveClick,
  buttonText,
  onButtonClick,
  customBgColor,
  roomIdPrefix = 'Room ID:',
  inviter,
  members,
  isDisabled = false,
  showDropdown = true,
  isProfessor = false,
  pendingAdjudicationsCount,
  advisorsCount,
  playersCount,
  negotiationType,
  isGameReadyToStart,
  scenarioTitle,
  isLocked = false,
  isOwner = false,
  isSimulating = false,
}) => {
  const getRandomBgColor = () => {
    return bgColors[Math.floor(Math.random() * bgColors.length)]
  }
  const displayId = roomId || memberEmail
  const bgColor = customBgColor || getRandomBgColor()

  const displayStatus =
    isSimulating && !isProfessor ? GameRoomStatus.NOT_STARTED : status

  const gameControls = useGameControls({
    roomId,
    scenarioTitle,
    isGameReadyToStart,
    status: status as GameRoomStatus,
    isSimulating,
  })

  const handleLockToggle = useCallback(() => {
    if (onLockedClick) {
      onLockedClick(roomId, !isLocked)
    }
  }, [onLockedClick, roomId, isLocked])

  // Only actions the caller actually wired appear in the menu. Cards that pass
  // a subset of handlers (e.g. Charter Class cards, which only support Delete)
  // would otherwise show dead entries that do nothing on click.
  const dropdownItems = useMemo(
    () =>
      [
        {
          icon: '/icons/pencil.svg',
          iconAlt: 'Edit',
          label: 'Edit Room',
          onClick: onEditClick,
        },
        {
          icon: '/icons/exit.svg',
          iconAlt: 'Leave',
          label: 'Leave Game',
          onClick: onLeaveClick,
        },
        ...(isOwner
          ? [
              {
                icon: '/icons/trash.svg',
                iconAlt: 'Delete',
                label: 'Delete Game',
                onClick: onDeleteClick,
              },
            ]
          : []),
        {
          icon: '/icons/duplicate.svg',
          iconAlt: 'Duplicate',
          label: 'Duplicate Game',
          onClick: onDuplicateClick,
        },
        {
          icon: '/icons/duplicate.svg',
          iconAlt: 'Export',
          label: 'Export Game',
          onClick: onExportClick,
        },
      ].filter((item) => item.onClick),
    [isOwner, onEditClick, onLeaveClick, onDeleteClick, onDuplicateClick, onExportClick]
  )

  const memberNamesDisplay = useMemo(() => {
    if (!members || members.length === 0) return null

    const nonOwnerMembers = members.filter((member) => !member.is_inviter)

    if (nonOwnerMembers.length === 0) return null

    const memberNames = nonOwnerMembers.map(
      (member) => member.name?.trim() || member.email
    )

    if (memberNames.length === 1) {
      return memberNames[0]
    } else if (memberNames.length === 2) {
      return `${memberNames[0]} & ${memberNames[1]}`
    } else if (memberNames.length <= 3) {
      return memberNames.join(', ')
    } else {
      return `${memberNames.slice(0, 2).join(', ')} & ${memberNames.length - 2} others`
    }
  }, [members])

  return (
    <div
      className={`flex flex-col min-h-[230px] ${bgColor} rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border border-white/10 relative group`}
    >
      <div className="absolute inset-0 bg-[url('/images/create-room-bg.png')] bg-cover bg-center opacity-40 transition-opacity group-hover:opacity-60"></div>

      {showBadge && (
        <div className='absolute z-10 top-3 left-3'>
          <StatusBadge status={displayStatus} />
        </div>
      )}
      <div className='absolute z-10 top-3 right-3 flex items-center gap-1'>
        {cardType === CardType.ROOM && isProfessor && isOwner && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleLockToggle}
                  className='flex items-center justify-center w-8 h-8 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 transition-all hover:bg-white/20'
                >
                  {isLocked ? (
                    <Lock className='w-4 h-4 text-white' />
                  ) : (
                    <LockOpen className='w-4 h-4 text-white' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isLocked
                    ? 'Game is locked. Unlock to allow deletion.'
                    : 'Click to Lock Room'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {isProfessor && showDropdown && (
          <OGECardDropdown
            isRoomLocked={isLocked}
            items={dropdownItems}
            isVisible={Boolean(
              onDeleteClick ||
                onEditClick ||
                onDuplicateClick ||
                onExportClick ||
                onLeaveClick
            )}
          />
        )}
      </div>

      <div className='z-[1] flex flex-col h-full p-5'>
        <div className='mt-10 mb-4 text-center'>
          {inviter && members && members.length > 0 ? (
            <span className='block mb-1 text-xs font-semibold text-white'>
              {negotiationType === NegotiationType.PUBLIC_ROOM
                ? 'Public Room'
                : `${inviter} & ${memberNamesDisplay}`}
            </span>
          ) : (
            <>
              {roomName && (
                <h3 className='mb-1 text-xl font-bold text-white transition-transform group-hover:scale-105'>
                  {roomName}
                </h3>
              )}
              <span className='block font-mono text-sm tracking-tight text-white/80 mb-1'>
                {roomIdPrefix} <span className='font-mono'>{displayId}</span>
              </span>
              {playingAs && (
                <div className='inline-flex items-center gap-2 px-3 py-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10'>
                  <User size={14} className='text-white/60' />
                  <span className='text-xs text-white/60'>As:</span>
                  <span className='font-mono text-sm font-medium text-white'>
                    {playingAs}
                  </span>
                </div>
              )}
              {inviteCode && isProfessor && (
                <div className='inline-flex items-center gap-2 px-2 py-1 bg-white/10 rounded border border-white/20'>
                  <code className='text-xs font-mono font-semibold text-white break-all'>
                    {inviteCode}
                  </code>
                </div>
              )}
            </>
          )}
        </div>

        <TooltipProvider>
          <div className='flex justify-center gap-4 mb-4'>
            {cardType !== CardType.ADVISOR_CHAT && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full cursor-help'>
                    <Users className='w-4 h-4 text-white' />
                    <span className='text-sm font-medium text-white'>
                      {playersCount}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Number of Players</p>
                </TooltipContent>
              </Tooltip>
            )}

            {cardType !== CardType.ADVISOR_CHAT && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full cursor-help'>
                    <Bot className='w-4 h-4 text-white' />
                    <span className='text-sm font-medium text-white'>
                      {advisorsCount}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Number of Advisors</p>
                </TooltipContent>
              </Tooltip>
            )}

            {isProfessor && cardType !== CardType.ADVISOR_CHAT && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full cursor-help'>
                    <Clock className='w-4 h-4 text-white' />
                    <span className='text-sm font-medium text-white'>
                      {pendingAdjudicationsCount}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Pending Adjudications</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>

        {cardType === CardType.ROOM && isProfessor && (
          <DashboardControls gameControls={gameControls} />
        )}

        <Button
          className={`w-full max-w-[200px] px-6 py-1.5 h-9 text-sm font-medium rounded-md transition-all mt-auto mx-auto ${
            isDisabled
              ? 'bg-disabled hover:bg-disabled text-zinc-500 cursor-not-allowed'
              : 'bg-white text-blue-800 hover:bg-blue-50 shadow-md hover:shadow-lg group-hover:translate-y-[-2px]'
          }`}
          onClick={!isDisabled ? onButtonClick : undefined}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  )
}

export default RoomCard
